// SAB-505 Phase 1: conventional NFL fantasy scoring (standard / half-PPR / PPR),
// frontend copy of supabase/functions/_shared/nfl-conventional-scoring.ts.
//
// This mirrors the MLB pattern where src/lib/trashScoring.ts duplicates the edge
// scoring so the client can render per-player points without a round-trip. Keep
// the weights here identical to the edge module.
//
// Scope: OFFENSE + KICKING only (DST/IDP deferred). Non-inverted: good real play
// scores positive. Sabotage leagues never use this — they read precomputed
// sabotage_points from nfl_player_game_stats.

export type NflConventionalMode = 'standard' | 'half_ppr' | 'ppr';

type WeightMap = Record<string, Record<string, number>>;

const OFFENSE_KICKING_BASE: WeightMap = {
  passing: { yards: 0.04, touchdowns: 4, interceptions: -2 },
  rushing: { yards: 0.1, touchdowns: 6 },
  receiving: { yards: 0.1, touchdowns: 6 },
  fumbles: { lost_fumbles: -2 },
  field_goals: { made_19: 3, made_29: 3, made_39: 3, made_49: 4, made_50: 5 },
};

function withReceptionValue(perReception: number): WeightMap {
  return {
    ...OFFENSE_KICKING_BASE,
    receiving: { ...OFFENSE_KICKING_BASE.receiving, receptions: perReception },
  };
}

export const NFL_CONVENTIONAL_PRESETS: Record<NflConventionalMode, WeightMap> = {
  standard: withReceptionValue(0),
  half_ppr: withReceptionValue(0.5),
  ppr: withReceptionValue(1),
};

export function isConventionalMode(scoringType: string | null | undefined): boolean {
  return scoringType === 'standard' || scoringType === 'half_ppr' || scoringType === 'ppr';
}

export function getConventionalPreset(scoringType: string): WeightMap {
  return NFL_CONVENTIONAL_PRESETS[(scoringType as NflConventionalMode)] ?? NFL_CONVENTIONAL_PRESETS.standard;
}

// Compute a single player's conventional points from their game-stats jsonb.
export function computeConventionalPoints(stats: any, weights: WeightMap): number {
  if (!stats || !weights) return 0;
  let pts = 0;
  for (const category of Object.keys(weights)) {
    const catStats = stats[category];
    if (!catStats || typeof catStats !== 'object') continue;
    const catWeights = weights[category];
    for (const statKey of Object.keys(catWeights)) {
      const v = Number(catStats[statKey]) || 0;
      pts += v * catWeights[statKey];
    }
  }
  return Math.round(pts * 100) / 100;
}
